<template>
  <div class="node-mdlists">
    <div class="header">
      <h3>Templates</h3>
    </div>

    <div class="add-existing-wrapper">
      <multiselect
        v-model="selectedMdListToAdd"
        :options="allMdLists"
        :searchable="true"
        label="name"
        track-by="id"
        placeholder="Add template"
      ></multiselect>
      <button
        :disabled="!selectedMdListToAdd"
        class="btn-primary"
        @click="attachMdListToNode"
      >
        Add
      </button>
    </div>

    <div v-if="entityMdLists.length === 0" class="empty-state">
      <p>No metadata lists are linked to this node yet.</p>
    </div>

    <div class="mdlist-panels">
      <MetadataListPanel
        v-for="mdList in entityMdLists"
        :key="mdList.id"
        :mdListId="mdList.id"
        :assType="assType"
        :assId="assId"
        @mdListDetached="handleMdListDetached"
        @mdListUpdated="handleMdListUpdated"
      />
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import axios from "axios";
import { mapState } from "vuex";
import MetadataListPanel from "./MetadataListPanel.vue";
import { API_ENDPOINTS } from "@/services/config";
import Multiselect from "vue-multiselect";
import { listEntityMdLists, attachMdListToEntity } from "@/services/mdlistBindingService";

export default {
  name: "NodeMdList",
  components: { MetadataListPanel, Multiselect },
  props: {
    assType: { type: String, required: true },
    assId: { type: Number, required: true },
    mdListId: { type: Number, default: null },
  },
  data() {
    return {
      entityMdLists: [],
      allMdLists: [],
      selectedMdListToAdd: "",
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  mounted() {
    this.refreshEntityMdLists();
    this.fetchAllCandidateMdLists();
  },
  watch: {
    mdListId() {
      this.refreshEntityMdLists();
    },
  },
  methods: {
    usesDirectEntityMetadataListBinding() {
      return ["instrument", "material"].includes(this.assType);
    },
    async loadDirectEntityMdLists() {
      if (!this.mdListId) {
        this.entityMdLists = [];
        return;
      }

      try {
        const mdList = await axios.get(API_ENDPOINTS.mdlist(this.mdListId), {
          params: {
            entity_type: this.assType,
            entra_id: this.loggedInUser?.entra_id,
          },
        });
        this.entityMdLists = mdList?.data ? [mdList.data] : [];
      } catch (error) {
        this.entityMdLists = [];
        this.toast.error("Failed to load metadata lists.");
        console.error(error);
      }
    },
    async refreshEntityMdLists() {
      if (this.usesDirectEntityMetadataListBinding()) {
        await this.loadDirectEntityMdLists();
        return;
      }

      try {
        const data = await listEntityMdLists(
          this.assType,
          this.assId,
          this.loggedInUser?.entra_id
        );
        this.entityMdLists = Array.isArray(data) ? data : [];
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.toast.error("Failed to load metadata lists.");
          console.error(error);
        }
      }
    },
    async fetchAllCandidateMdLists() {
      try {
        const response = await axios.get(API_ENDPOINTS.mdlists, {
          params: { entity_type: this.assType },
        });
        this.allMdLists = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.toast.error("Failed to fetch available metadata lists.");
        console.error(error);
      }
    },
    async attachMdListToNode() {
      if (!this.selectedMdListToAdd) return;

      if (this.usesDirectEntityMetadataListBinding()) {
        this.$emit("mdListCreated", this.selectedMdListToAdd);
        this.selectedMdListToAdd = "";
        return;
      }

      try {
        await attachMdListToEntity(
          this.assType,
          this.assId,
          this.selectedMdListToAdd.id,
          this.loggedInUser?.entra_id
        );
        this.toast.success("Metadata list attached.");
        this.selectedMdListToAdd = "";
        await this.refreshEntityMdLists();
      } catch (error) {
        this.toast.error("Failed to attach metadata list.");
        console.error(error);
      }
    },
    handleMdListDetached() {
      if (this.usesDirectEntityMetadataListBinding()) {
        this.entityMdLists = [];
        this.$emit("mdListDetached");
        return;
      }

      this.refreshEntityMdLists();
    },
    handleMdListUpdated() {
      this.$emit("mdListUpdated");
      this.refreshEntityMdLists();
    },
  },
};
</script>

<style scoped>
.header { margin-bottom: 10px; }
.add-existing-wrapper {
  display: flex; gap: 8px; align-items: center; margin-bottom: 16px;
}
.btn-primary { padding: 6px 10px; }
.empty-state { color: #666; margin: 10px 0; }
.mdlist-panels { display: grid; gap: 14px; }
</style>
